'use client';

import { useSyncExternalStore } from 'react';
import { formatPrice, formatPriceFull, titleCaseCity, cn } from '@/lib/utils';
import { availabilityOf, badgeClasses, badgeLabel } from '@/lib/availability';
import { listingViewTransitionName } from '@/lib/listingViewTransition';
import FavoriteButton from './FavoriteButton';
import type { ListingStatus } from '@/types/listing';

const REDUCED_MOTION_QUERY = '(prefers-reduced-motion: reduce)';
const subscribeReducedMotion = (onChange: () => void) => {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return () => {};
  const media = window.matchMedia(REDUCED_MOTION_QUERY);
  media.addEventListener?.('change', onChange);
  return () => media.removeEventListener?.('change', onChange);
};
const reducedMotionSnapshot = () =>
  typeof window !== 'undefined' &&
  typeof window.matchMedia === 'function' &&
  window.matchMedia(REDUCED_MOTION_QUERY).matches;
const serverReducedMotionSnapshot = () => false;

interface ListingHeaderProps {
  listingId: string;
  /** Public MLS identity shared with the source card transition boundary. */
  mlsListingId?: string;
  price: number;
  originalListPrice?: number;
  address: string;
  city: string;
  state: string;
  zip: string;
  status: ListingStatus;
}

export default function ListingHeader({
  listingId,
  mlsListingId,
  price,
  originalListPrice,
  address,
  city,
  state,
  zip,
  status,
}: ListingHeaderProps) {
  const reducedMotion = useSyncExternalStore(
    subscribeReducedMotion,
    reducedMotionSnapshot,
    serverReducedMotionSnapshot
  );
  const transitionName = reducedMotion
    ? undefined
    : listingViewTransitionName(mlsListingId, 'meta');
  // One vocabulary for availability across every surface (`lib/availability`),
  // so the pill beside the price cannot say "Expired" while the banner above it
  // says "No longer listed" and the card said something else again. (W16 R151)
  const availability = availabilityOf(status);
  const statusBadge = badgeLabel(availability);
  return (
    <div
      className="mb-4 flex items-start justify-between gap-4"
      style={transitionName ? { viewTransitionName: transitionName } : undefined}
      data-view-transition-name={transitionName}
    >
      <div>
        <p className="flex flex-wrap items-baseline gap-3 font-display text-4xl tabular-nums tracking-tight text-ink sm:text-5xl">
          {formatPriceFull(price)}
          {(originalListPrice ?? 0) > price && (
            <span className="flex items-baseline gap-2 font-sans">
              <s className="text-lg text-ink-faint">{formatPriceFull(originalListPrice!)}</s>
              <span className="rounded-full border border-gold-500/25 bg-gold-500/10 px-2.5 py-1 text-sm font-semibold tabular-nums text-gold-300">
                ▼ {formatPrice(originalListPrice! - price)} price cut
              </span>
            </span>
          )}
        </p>
        <h1 className="mt-2 text-lg text-ink-soft">
          {address}, {titleCaseCity(city)}, {state} {zip}
        </h1>
      </div>

      <div className="flex items-center gap-3">
        <FavoriteButton listingId={listingId} size="lg" />

        {statusBadge && (
          <span
            className={cn(
              'whitespace-nowrap rounded-full px-3 py-1 text-sm font-semibold',
              badgeClasses(availability)
            )}
          >
            {statusBadge}
          </span>
        )}
      </div>
    </div>
  );
}
